/**
 * /payroll-l4/certified-payroll-reports — WH-347 list (Step 6).
 *
 * Lists every certified-payroll report for the active entity. Lets you
 * generate a new report for a (project, week_ending) tuple, drill into
 * the detail page, or download the rendered PDF inline.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { api, getToken, errorMessage } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { useToast } from "@/components/Toaster";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";
import type {
  WH347Report,
  WH347ReportFull,
  WH347Status,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/payroll-l4/certified-payroll-reports")({
  component: WH347ListPage,
});

const STATUS_COLOR: Record<WH347Status, "default" | "success" | "warning" | "error" | "info"> = {
  draft: "default",
  generated: "info",
  signed: "success",
  submitted: "info",
  accepted: "success",
  rejected: "error",
  superseded: "warning",
};

function WH347ListPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [projectFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<WH347Status | "">("");
  const [generateOpen, setGenerateOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(
    () => localStorage.getItem("cp_guide_dismissed") !== "1",
  );
  const dismissGuide = () => {
    localStorage.setItem("cp_guide_dismissed", "1");
    setGuideOpen(false);
  };

  const { data: reports = [], isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "wh347", projectFilter, statusFilter],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (projectFilter.trim()) qs.set("project_id", projectFilter.trim());
      if (statusFilter) qs.set("status_filter", statusFilter);
      return (await api(
        `/api/payroll-l4/certified-payroll-reports${qs.toString() ? "?" + qs : ""}`,
      )) as WH347Report[];
    },
  });

  // Authenticated PDF download — fetch with Bearer token, then open the
  // resulting object URL in a new tab. Matching the existing payroll
  // year-end download pattern.
  const downloadPdf = async (id: string, weekEnding: string, projectCode: string | null) => {
    try {
      const r = await fetch(`/api/payroll-l4/certified-payroll-reports/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WH-347_${projectCode ?? "report"}_${weekEnding}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`PDF download failed: ${(e as Error).message}`);
    }
  };

  // Generic authenticated file download for XML/CSV exports.
  const downloadExport = async (
    id: string,
    format: "dir" | "lcptracker" | "emars",
    weekEnding: string,
    projectCode: string | null,
  ) => {
    const ext = format === "emars" ? "csv" : "xml";
    const label = format === "dir" ? "CA-DIR-eCPR" : format === "lcptracker" ? "LCPtracker" : "eMars";
    try {
      const r = await fetch(
        `/api/payroll-l4/certified-payroll-reports/${id}/export/${format}`,
        { headers: { Authorization: `Bearer ${getToken() ?? ""}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}_${projectCode ?? "report"}_${weekEnding}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`${label} export failed: ${(e as Error).message}`);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Certified Payroll Reports (WH-347)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            The weekly WH-347 form you file for prevailing-wage (Davis-Bacon)
            jobs. Generate one from an <strong>approved</strong> pay run, download
            the PDF, sign the Statement of Compliance, then submit it to your
            agency.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint
              docPath="payroll-l4/step-6-wh347-pdf.md"
              label="WH-347 generation help"
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select label="Status" size="small" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WH347Status | "")}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="generated">Generated</MenuItem>
            <MenuItem value="signed">Signed</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="superseded">Superseded</MenuItem>
          </TextField>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate WH-347 PDF
          </Button>
        </Stack>
      </Stack>

      {guideOpen ? (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, borderColor: "primary.main", bgcolor: "action.hover" }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              How certified payroll works — the 5 steps
            </Typography>
            <Button size="small" onClick={dismissGuide}>Got it</Button>
          </Stack>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 3,
              listStyleType: "decimal",
              color: "text.secondary",
              fontSize: 14,
              "& li": { mb: 0.5, "&::marker": { fontWeight: 700, color: "text.primary" } },
              "& a": { color: "primary.main", fontWeight: 600, textDecoration: "none" },
              "& a:hover": { textDecoration: "underline" },
            }}
          >
            <li>
              <Link to="/payroll-l4/wage-determinations">Set up the wage determination</Link>{" "}
              — upload the prevailing-wage PDF and make sure its rates are active.
            </li>
            <li>
              <Link to="/projects">Turn on Davis-Bacon on your project</Link>{" "}
              — assign that wage determination and map each craft to a classification.
            </li>
            <li>
              <Link to="/payroll/pay-runs">Enter the crew's hours and run payroll</Link>{" "}
              — calculate the pay run for the week.
            </li>
            <li>
              <Link to="/payroll-l4/compliance">Clear any prevailing-wage shortfalls</Link>{" "}
              — Auto-fix or override every blocking item, then approve the pay run.
            </li>
            <li>
              <strong>Generate the WH-347 here</strong> — then sign the Statement
              of Compliance and submit it to your agency.
            </li>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ mb: 1 }}>
          <Button size="small" onClick={() => setGuideOpen(true)}>
            Show the 5-step guide
          </Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(error)}</Alert>}

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell align="right">Payroll #</TableCell>
                <TableCell>Week ending</TableCell>
                <TableCell align="right">Workers</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">Gross (this proj)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <strong>{r.project_code ?? r.project_id.slice(0, 8)}</strong>
                    {r.project_name && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {r.project_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {r.payroll_number}
                    {r.is_final && <Chip size="small" label="FINAL" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{r.week_ending}</TableCell>
                  <TableCell align="right">{r.total_employees}</TableCell>
                  <TableCell align="right">{r.total_hours}</TableCell>
                  <TableCell align="right">${r.total_gross_wages}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon fontSize="small" />}
                        title="Download a print-ready WH-347 PDF to sign by hand"
                        onClick={() => downloadPdf(r.id, r.week_ending, r.project_code)}
                      >
                        PDF
                      </Button>
                      <IconButton
                        size="small"
                        component={Link}
                        title="Open detail"
                        to={"/payroll-l4/certified-payroll-reports/$reportId" as never}
                        params={{ reportId: r.id } as never}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Export CA DIR eCPR XML"
                        onClick={() => downloadExport(r.id, "dir", r.week_ending, r.project_code)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No reports yet. Use the <strong>Generate WH-347 PDF</strong> button (top right) to build your first one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <GenerateDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onSuccess={(rep) => {
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
          setGenerateOpen(false);
          toast.success(
            `Report #${rep.payroll_number} generated for ${rep.week_ending} — downloading the print-ready PDF.`,
          );
          // The user clicked "Generate" expecting a PDF — hand it to them.
          void downloadPdf(rep.id, rep.week_ending, rep.project_code);
        }}
      />
    </Box>
  );
}

// WH-347 weeks run Mon–Sun, so the "week ending" is always a Sunday. Rather
// than default to today (usually a weekday) and reject it, we compute Sundays
// for the user: the field starts on the most recent past Sunday (the last
// completed week — what you normally report), and any date the user picks
// snaps forward to the Sunday that ends its Mon–Sun week. The human never has
// to figure out which Sunday it is.
function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function mostRecentSunday(): string {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay()); // getDay(): Sun=0 → lands on Sun (today if Sunday)
  return fmtLocalDate(d);
}
function weekEndingSunday(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate;
  const [y, m, day] = isoDate.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7)); // forward to the week's ending Sunday
  return fmtLocalDate(d);
}

function GenerateDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (rep: WH347ReportFull) => void;
}) {
  const [projectId, setProjectId] = useState("");
  const [weekEnding, setWeekEnding] = useState(() => mostRecentSunday());

  const generate = useMutation({
    mutationFn: async () =>
      (await api(`/api/payroll-l4/certified-payroll-reports/generate`, {
        method: "POST",
        body: { project_id: projectId, week_ending: weekEnding },
      })) as WH347ReportFull,
    onSuccess,
  });

  return (
    <Dialog open={open} onClose={() => !generate.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Generate WH-347 PDF</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick the Davis-Bacon project and the Sunday that ends the week. We
          build the WH-347 from that week's hours and paychecks automatically,
          then <strong>download a print-ready PDF</strong> you can sign by hand.
          <br />
          <strong>Before you generate:</strong> the project's pay run for this
          week must be <strong>approved</strong>. Re-generating for the same
          project and week refreshes the report in place.
        </Typography>
        <Stack spacing={2}>
          <ProjectIdSelect
            label="Project"
            size="small"
            fullWidth
            value={projectId}
            onChange={setProjectId}
            disabled={generate.isPending}
            statusFilter={STANDARD_PROJECT_STATUSES}
            davisBaconEnabled
            required
            placeholder="Search Davis-Bacon projects…"
          />
          <TextField
            label="Week ending (Sunday)"
            type="date"
            size="small"
            fullWidth
            value={weekEnding}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setWeekEnding(e.target.value ? weekEndingSunday(e.target.value) : "")}
            disabled={generate.isPending}
            helperText="Pick any day in the work week — we snap to the Sunday that ends it (WH-347 weeks run Mon–Sun)."
          />
        </Stack>
        {generate.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(generate.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generate.isPending}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={!generate.isPending ? <PictureAsPdfIcon /> : undefined}
          disabled={!projectId || !weekEnding || generate.isPending}
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? <CircularProgress size={18} /> : "Generate & Download PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
